import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Container,
  Paper
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { AgentType, AGENTS } from '../types/auth';

interface AgentSelectionProps {
  onAgentSelect: (agentType: AgentType) => void;
  userRole: 'super_admin' | 'admin';
}

const AgentSelection: React.FC<AgentSelectionProps> = ({ onAgentSelect, userRole }) => {
  const handleAgentSelect = (agentType: AgentType) => {
    onAgentSelect(agentType);
  };

  const getAgentIcon = (agentType: AgentType) => {
    switch (agentType) {
      case 'NA':
        return <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />;
      case 'PA':
        return <LocationIcon sx={{ fontSize: 40, color: 'secondary.main' }} />;
      case 'TMA':
        return <PersonIcon sx={{ fontSize: 40, color: 'success.main' }} />;
      default:
        return <PersonIcon sx={{ fontSize: 40, color: 'grey.500' }} />;
    }
  };

  const getAgentColor = (agentType: AgentType) => {
    switch (agentType) {
      case 'NA':
        return 'primary.main';
      case 'PA':
        return 'secondary.main';
      case 'TMA':
        return 'success.main';
      default:
        return 'grey.500';
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom color="primary">
          Welcome to Milk Collection System
        </Typography>
        
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          {userRole === 'super_admin' 
            ? 'Select your role to continue' 
            : 'Please select your agent type to continue'
          }
        </Typography>

        {userRole === 'super_admin' && (
          <Box sx={{ mb: 4 }}>
            <Card 
              sx={{ 
                maxWidth: 300, 
                mx: 'auto',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => onAgentSelect('NA')} // Super admin can select any agent
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <BusinessIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Super Administrator
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Full access to all system features including ledger management and client modifications.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {userRole === 'admin' && (
          <Grid container spacing={3} justifyContent="center">
            {Object.values(AGENTS).map((agent) => (
              <Grid item xs={12} sm={6} md={4} key={agent.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: `2px solid ${getAgentColor(agent.id)}`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      borderColor: getAgentColor(agent.id)
                    }
                  }}
                  onClick={() => handleAgentSelect(agent.id)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    {getAgentIcon(agent.id)}
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      {agent.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Operator ID: {agent.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      You can add new clients and view sales data for your assigned collection center.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Box sx={{ mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {userRole === 'super_admin' 
              ? 'As a Super Administrator, you have access to all system features.'
              : 'As an Agent, you can add new clients and view data for your assigned collection center.'
            }
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default AgentSelection;
